import React, { useState, useEffect } from 'react'
import s from './LoadsPanel.module.scss'
import { getLoadsInLoading, getLoadsInRoute, getLoadsCompleted, markExit, updateLoadNotes, completeTrip, getLoadsTimeline } from 'service/LoadService'
import moment from 'moment'
import Notification from 'utils/Notifications'

// ─── Small helper components ──────────────────────────────────────────────────

const GeoBtn = ({ geo, label }) => {
  if (!geo) return <span className={s.noneVal}>—</span>
  return (
    <button
      className={s.geoBtn}
      onClick={() => window.open(`https://www.google.com/maps?q=${geo}`, '_blank')}
    >
      📍 {label || 'Карта'}
    </button>
  )
}

const PhotoBtn = ({ url, label }) => {
  if (!url) return <span className={s.noneVal}>—</span>
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className={s.photoLink}>
      🖼 {label || 'Фото'}
    </a>
  )
}

const DetailCard = ({ title, value, sub, wide }) => (
  <div className={`${s.detailCard}${wide ? ` ${s.wide}` : ''}`}>
    <div className={s.detailCardLabel}>{title}</div>
    <div className={s.detailCardValue}>
      {value ?? <span className={s.noneVal}>—</span>}
    </div>
    {sub && <div className={s.detailCardSub}>{sub}</div>}
  </div>
)

const PAGE_SIZE = 25

const LoadsPanel = () => {
  const [subTab, setSubTab] = useState('in_loading') // 'in_loading' | 'in_route' | 'history'

  // В погрузке
  const [loadingLoads, setLoadingLoads] = useState([])
  const [loadingLoadsPage, setLoadingLoadsPage] = useState(1)
  const [loadingLoadsCount, setLoadingLoadsCount] = useState(0)
  const [loadingLoadsSearch, setLoadingLoadsSearch] = useState('')

  // В пути
  const [activeLoads, setActiveLoads] = useState([])
  const [activeLoadsPage, setActiveLoadsPage] = useState(1)
  const [activeLoadsCount, setActiveLoadsCount] = useState(0)
  const [activeLoadsSearch, setActiveLoadsSearch] = useState('')

  // История
  const [historyLoads, setHistoryLoads] = useState([])
  const [historyLoadsPage, setHistoryLoadsPage] = useState(1)
  const [historyLoadsCount, setHistoryLoadsCount] = useState(0)
  const [historyLoadsSearch, setHistoryLoadsSearch] = useState('')

  // Раскрытые строки
  const [expandedRows, setExpandedRows] = useState({})

  // Геолокация
  const [userGeoLocation, setUserGeoLocation] = useState(null)

  // ─── Timeline ──────────────────────────────────────────────────────────────
  const todayStr = new Date().toISOString().split('T')[0]
  const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0] }
  const [timeline, setTimeline] = useState(null)
  const [tlDateFrom, setTlDateFrom] = useState(daysAgo(14))
  const [tlDateTo, setTlDateTo] = useState(todayStr)
  const [tlPreset, setTlPreset] = useState('15d')
  const [tlLoading, setTlLoading] = useState(false)

  const TL_PRESETS = [
    { key: '15d',  label: '15 дней',     days: 14 },
    { key: '30d',  label: 'За месяц',   days: 29 },
    { key: '90d',  label: '3 месяца',   days: 89 },
    { key: '180d', label: '6 месяцев',  days: 179 },
    { key: '365d', label: 'Год',        days: 364 },
  ]

  const applyPreset = (preset) => {
    const from = daysAgo(preset.days)
    setTlDateFrom(from)
    setTlDateTo(todayStr)
    setTlPreset(preset.key)
    loadTimeline(from, todayStr)
  }

  // Модалки
  const [notesModal, setNotesModal] = useState({ isOpen: false, loadId: null })
  const [notesInput, setNotesInput] = useState('')
  const [notesSaving, setNotesSaving] = useState(false)

  const [completeTripModal, setCompleteTripModal] = useState({ isOpen: false, loadId: null })
  const [completeTripNotes, setCompleteTripNotes] = useState('')
  const [completeTripSaving, setCompleteTripSaving] = useState(false)

  const [markExitModal, setMarkExitModal] = useState({ isOpen: false, loadId: null })
  const [markExitSaving, setMarkExitSaving] = useState(false)

  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  // ─── Loaders ─────────────────────────────────────────────────────────────────

  const loadTimeline = async (from, to) => {
    setTlLoading(true)
    try {
      const res = await getLoadsTimeline({ date_from: from, date_to: to })
      setTimeline(res.data)
    } catch (err) { console.error(err) }
    finally { setTlLoading(false) }
  }

  const loadInLoadingLoads = async (page = 1, search = '') => {
    try {
      const res = await getLoadsInLoading({ page, page_size: PAGE_SIZE, ...(search ? { search } : {}) })
      setLoadingLoads(res.data.results || [])
      setLoadingLoadsCount(res.data.count || 0)
      setLoadingLoadsPage(page)
    } catch (err) { console.error(err) }
  }

  const loadActiveLoads = async (page = 1, search = '') => {
    try {
      const res = await getLoadsInRoute({ page, page_size: PAGE_SIZE, ...(search ? { search } : {}) })
      setActiveLoads(res.data.results || [])
      setActiveLoadsCount(res.data.count || 0)
      setActiveLoadsPage(page)
    } catch (err) { console.error(err) }
  }

  const loadHistoryLoads = async (page = 1, search = '') => {
    try {
      const res = await getLoadsCompleted({ page, page_size: PAGE_SIZE, ...(search ? { search } : {}) })
      setHistoryLoads(res.data.results || [])
      setHistoryLoadsCount(res.data.count || 0)
      setHistoryLoadsPage(page)
    } catch (err) { console.error(err) }
  }

  const reloadCurrent = async () => {
    if (subTab === 'in_loading') await loadInLoadingLoads(loadingLoadsPage, loadingLoadsSearch)
    else if (subTab === 'in_route') await loadActiveLoads(activeLoadsPage, activeLoadsSearch)
    else await loadHistoryLoads(historyLoadsPage, historyLoadsSearch)
  }

  useEffect(() => {
    loadTimeline(tlDateFrom, tlDateTo)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (subTab === 'in_loading') loadInLoadingLoads(1, loadingLoadsSearch)
    else if (subTab === 'in_route') loadActiveLoads(1, activeLoadsSearch)
    else loadHistoryLoads(1, historyLoadsSearch)
    setExpandedRows({})
  }, [subTab])

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserGeoLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => console.warn('Geolocation:', err)
      )
    }
  }, [])

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const toggleRow = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }))

  const elapsedSince = (timeStr) => {
    if (!timeStr) return null
    const start = moment(timeStr, 'DD.MM.YYYY HH:mm')
    if (!start.isValid()) return null
    const diff = moment.duration(moment().diff(start))
    const h = Math.floor(diff.asHours())
    const m = diff.minutes()
    return h > 0 ? `${h}ч ${m}мин` : `${m}мин`
  }

  const geoStr = () => userGeoLocation ? `${userGeoLocation.lat},${userGeoLocation.lng}` : ''

  // Parse admin note prefix added by backend
  const parseCompletedBy = (item) => {
    const adminMatch = item.notes ? item.notes.match(/^\[Завершил: ([^\]]+)\]/) : null
    return item.operator_name || (adminMatch ? `${adminMatch[1]} (Адм.)` : null)
  }

  const parseNotesClean = (notes) => notes ? notes.replace(/^\[Завершил: [^\]]+\]\s*/, '') : ''

  // ─── Modal actions ────────────────────────────────────────────────────────────

  const openNotesModal = (loadId, currentNotes) => {
    setNotesModal({ isOpen: true, loadId })
    setNotesInput(currentNotes || '')
  }

  const saveNotes = async () => {
    setNotesSaving(true)
    try {
      await updateLoadNotes(notesModal.loadId, notesInput)
      await reloadCurrent()
      setNotesModal({ isOpen: false, loadId: null })
      setNotesInput('')
      setNotify({ isOpen: true, message: 'Примечание сохранено', type: 'success' })
    } catch { setNotify({ isOpen: true, message: 'Ошибка сохранения', type: 'error' }) }
    finally { setNotesSaving(false) }
  }

  const handleCompleteTrip = async () => {
    if (!completeTripNotes.trim()) {
      setNotify({ isOpen: true, message: 'Примечание обязательно', type: 'error' })
      return
    }
    setCompleteTripSaving(true)
    try {
      await completeTrip(completeTripModal.loadId, completeTripNotes, geoStr())
      await reloadCurrent()
      setCompleteTripModal({ isOpen: false, loadId: null })
      setCompleteTripNotes('')
      setNotify({ isOpen: true, message: 'Поездка завершена', type: 'success' })
    } catch (err) { setNotify({ isOpen: true, message: err.response?.data?.error || 'Ошибка завершения', type: 'error' }) }
    finally { setCompleteTripSaving(false) }
  }

  const handleMarkExit = async () => {
    setMarkExitSaving(true)
    try {
      await markExit(markExitModal.loadId, { geo_a_exit: geoStr() })
      await loadInLoadingLoads(loadingLoadsPage, loadingLoadsSearch)
      setMarkExitModal({ isOpen: false, loadId: null })
      setNotify({ isOpen: true, message: 'Выезд отмечен', type: 'success' })
    } catch (err) { setNotify({ isOpen: true, message: err.response?.data?.error || 'Ошибка выезда', type: 'error' }) }
    finally { setMarkExitSaving(false) }
  }

  // ─── UI Pieces ────────────────────────────────────────────────────────────────

  const Pagination = ({ page, count, onPage }) => {
    const pages = Math.ceil(count / PAGE_SIZE)
    if (pages <= 1) return null
    return (
      <div className={s.pagination}>
        <button className={s.paginationBtn} disabled={page === 1} onClick={() => onPage(page - 1)}>← Пред.</button>
        <span className={s.paginationInfo}>Стр. {page} / {pages} · {count} записей</span>
        <button className={s.paginationBtn} disabled={page >= pages} onClick={() => onPage(page + 1)}>След. →</button>
      </div>
    )
  }

  const SearchBar = ({ value, onChange, onSearch, onRefresh }) => (
    <div className={s.searchBar}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onSearch() }}
        placeholder="Поиск по водителю, базе, номеру машины..."
        className={s.searchInput}
      />
      <button className={s.searchIconBtn} onClick={onSearch}>🔍</button>
      <button className={s.refreshIconBtn} onClick={onRefresh}>↻</button>
    </div>
  )

  const ActionBtns = ({ row, tab }) => (
    <div className={s.actionBtns}>
      {tab === 'in_loading' && (
        <button className={s.btnExit} onClick={() => setMarkExitModal({ isOpen: true, loadId: row.id })}>
          Выезд
        </button>
      )}
      {tab !== 'history' && (
        <button className={s.btnComplete} onClick={() => { setCompleteTripModal({ isOpen: true, loadId: row.id }); setCompleteTripNotes('') }}>
          Завершить
        </button>
      )}
      <button className={s.btnNotes} onClick={() => openNotesModal(row.id, row.notes)}>📝 Заметка</button>
    </div>
  )

  // ─── Tab: В погрузке ─────────────────────────────────────────────────────────

  const renderLoadingLoads = () => (
    <>
      <SearchBar
        value={loadingLoadsSearch} onChange={setLoadingLoadsSearch}
        onSearch={() => loadInLoadingLoads(1, loadingLoadsSearch)}
        onRefresh={() => loadInLoadingLoads(loadingLoadsPage, loadingLoadsSearch)}
      />
      <div className={s.tableWrapper}>
        <table className={s.loadsTable}>
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <th>Водитель</th>
              <th>Телефон</th>
              <th>Авто / Модель</th>
              <th>База</th>
              <th>Въезд (А)</th>
              <th>Охранник въезда</th>
              <th>В погрузке</th>
              <th style={{ width: 36 }}></th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(loadingLoads) ? loadingLoads : []).map((item, index) => {
              const seq = (loadingLoadsPage - 1) * PAGE_SIZE + index + 1
              const elapsed = elapsedSince(item.time_a)
              const isExpanded = !!expandedRows[item.id]
              return (
                <React.Fragment key={item.id}>
                  <tr
                    className={`${s.dataRow}${isExpanded ? ` ${s.rowExpanded}` : ''}`}
                    onClick={() => toggleRow(item.id)}
                  >
                    <td>{seq}</td>
                    <td><span className={s.driverName}>{item.employee_name || '—'}</span></td>
                    <td><span className={s.phoneText}>{item.employee_phone || '—'}</span></td>
                    <td>
                      <div className={s.vehicleNumber}>{item.vehicle_number || '—'}</div>
                      <div className={s.vehicleModel}>{item.vehicle_model || ''}</div>
                    </td>
                    <td><span className={s.baseName}>{item.top_base_name || item.chpp_name || item.private_point_name || '—'}</span></td>
                    <td><span className={s.timeText}>{item.time_a || '—'}</span></td>
                    <td><span className={s.phoneText}>{item.guard_name || '—'}</span></td>
                    <td><span className={s.elapsedBlue}>{elapsed || '—'}</span></td>
                    <td className={s.expandToggle}>{isExpanded ? '▲' : '▼'}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <ActionBtns row={item} tab="in_loading" />
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className={s.detailRow}>
                      <td colSpan={10}>
                        <div className={s.expandDetail}>
                          <div className={s.detailGrid}>
                            <DetailCard title="ID записи" value={`#${item.id}`} />
                            <DetailCard title="Охранник въезда" value={item.guard_name} sub={item.guard_phone} />
                            <DetailCard title="Гео въезда (А)" value={<GeoBtn geo={item.geo_a} label="Открыть" />} />
                            <DetailCard title="Фото въезда (А)" value={<PhotoBtn url={item.photo_a} label="Смотреть" />} />
                            <DetailCard title="Статус водителя" value={item.driver_status} />
                            <DetailCard title="Создана" value={item.created_at ? moment(item.created_at).format('DD.MM.YYYY HH:mm') : null} />
                            {item.notes && <DetailCard title="Примечания" value={parseNotesClean(item.notes)} wide />}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
            {loadingLoads.length === 0 && (
              <tr className={s.emptyRow}>
                <td colSpan={10}>Нет погрузок со статусом «В погрузке»</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={loadingLoadsPage} count={loadingLoadsCount} onPage={(p) => loadInLoadingLoads(p, loadingLoadsSearch)} />
    </>
  )

  // ─── Tab: В пути ─────────────────────────────────────────────────────────────

  const renderActiveLoads = () => (
    <>
      <SearchBar
        value={activeLoadsSearch} onChange={setActiveLoadsSearch}
        onSearch={() => loadActiveLoads(1, activeLoadsSearch)}
        onRefresh={() => loadActiveLoads(activeLoadsPage, activeLoadsSearch)}
      />
      <div className={s.tableWrapper}>
        <table className={s.loadsTable}>
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <th>Водитель</th>
              <th>Телефон</th>
              <th>Авто / Модель</th>
              <th>База</th>
              <th>Въезд (А)</th>
              <th>Выезд (А)</th>
              <th>Охранник выезда</th>
              <th>В пути</th>
              <th style={{ width: 36 }}></th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(activeLoads) ? activeLoads : []).map((item, index) => {
              const seq = (activeLoadsPage - 1) * PAGE_SIZE + index + 1
              const elapsed = elapsedSince(item.time_a_exit)
              const isExpanded = !!expandedRows[item.id]
              return (
                <React.Fragment key={item.id}>
                  <tr
                    className={`${s.dataRow}${isExpanded ? ` ${s.rowExpanded}` : ''}`}
                    onClick={() => toggleRow(item.id)}
                  >
                    <td>{seq}</td>
                    <td><span className={s.driverName}>{item.employee_name || '—'}</span></td>
                    <td><span className={s.phoneText}>{item.employee_phone || '—'}</span></td>
                    <td>
                      <div className={s.vehicleNumber}>{item.vehicle_number || '—'}</div>
                      <div className={s.vehicleModel}>{item.vehicle_model || ''}</div>
                    </td>
                    <td><span className={s.baseName}>{item.top_base_name || item.chpp_name || item.private_point_name || '—'}</span></td>
                    <td><span className={s.timeText}>{item.time_a || '—'}</span></td>
                    <td><span className={s.timeText} style={{ fontWeight: 700 }}>{item.time_a_exit || '—'}</span></td>
                    <td><span className={s.phoneText}>{item.exit_guard_name || '—'}</span></td>
                    <td><span className={s.elapsedAmber}>{elapsed || '—'}</span></td>
                    <td className={s.expandToggle}>{isExpanded ? '▲' : '▼'}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <ActionBtns row={item} tab="in_route" />
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className={s.detailRow}>
                      <td colSpan={11}>
                        <div className={s.expandDetail}>
                          <div className={s.detailGrid}>
                            <DetailCard title="ID записи" value={`#${item.id}`} />
                            <DetailCard title="Охранник въезда (А)" value={item.guard_name} sub={item.guard_phone} />
                            <DetailCard title="Охранник выезда (А)" value={item.exit_guard_name} sub={item.exit_guard_phone} />
                            <DetailCard title="Время погрузки" value={item.duration_str_loading} />
                            <DetailCard title="Гео въезда (А)" value={<GeoBtn geo={item.geo_a} label="Открыть" />} />
                            <DetailCard title="Гео выезда (А)" value={<GeoBtn geo={item.geo_a_exit} label="Открыть" />} />
                            <DetailCard title="Фото въезда (А)" value={<PhotoBtn url={item.photo_a} label="Смотреть" />} />
                            <DetailCard title="Статус водителя" value={item.driver_status} />
                            {item.notes && <DetailCard title="Примечания" value={parseNotesClean(item.notes)} wide />}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
            {activeLoads.length === 0 && (
              <tr className={s.emptyRow}>
                <td colSpan={11}>Нет погрузок со статусом «В пути»</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={activeLoadsPage} count={activeLoadsCount} onPage={(p) => loadActiveLoads(p, activeLoadsSearch)} />
    </>
  )

  // ─── Tab: История ─────────────────────────────────────────────────────────────

  const renderHistoryLoads = () => (
    <>
      <SearchBar
        value={historyLoadsSearch} onChange={setHistoryLoadsSearch}
        onSearch={() => loadHistoryLoads(1, historyLoadsSearch)}
        onRefresh={() => loadHistoryLoads(historyLoadsPage, historyLoadsSearch)}
      />
      <div className={s.tableWrapper}>
        <table className={s.loadsTable}>
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <th>Водитель</th>
              <th>Телефон</th>
              <th>Авто</th>
              <th>База</th>
              <th>Въезд (А)</th>
              <th>Выезд (А)</th>
              <th>Прибытие (Б)</th>
              <th>Погрузка</th>
              <th>Доставка</th>
              <th>Итого</th>
              <th>Кто завершил</th>
              <th style={{ width: 36 }}></th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(historyLoads) ? historyLoads : []).map((item, index) => {
              const seq = (historyLoadsPage - 1) * PAGE_SIZE + index + 1
              const isExpanded = !!expandedRows[item.id]
              const completedBy = parseCompletedBy(item)
              const notesClean = parseNotesClean(item.notes)
              return (
                <React.Fragment key={item.id}>
                  <tr
                    className={`${s.dataRow}${isExpanded ? ` ${s.rowExpanded}` : ''}`}
                    onClick={() => toggleRow(item.id)}
                  >
                    <td>{seq}</td>
                    <td><span className={s.driverName}>{item.employee_name || '—'}</span></td>
                    <td><span className={s.phoneText}>{item.employee_phone || '—'}</span></td>
                    <td>
                      <div className={s.vehicleNumber}>{item.vehicle_number || '—'}</div>
                      <div className={s.vehicleModel}>{item.vehicle_model || ''}</div>
                    </td>
                    <td><span className={s.baseName}>{item.top_base_name || item.chpp_name || item.private_point_name || '—'}</span></td>
                    <td><span className={s.timeText}>{item.time_a || '—'}</span></td>
                    <td><span className={s.timeText}>{item.time_a_exit || '—'}</span></td>
                    <td><span className={s.elapsedGreen}>{item.time_b || '—'}</span></td>
                    <td><span className={s.durationText}>{item.duration_str_loading || '—'}</span></td>
                    <td><span className={s.durationText}>{item.duration_str || '—'}</span></td>
                    <td><span className={s.durationText} style={{ fontWeight: 700 }}>{item.total_duration_str || '—'}</span></td>
                    <td><span className={completedBy ? s.completedBy : s.noneVal}>{completedBy || '—'}</span></td>
                    <td className={s.expandToggle}>{isExpanded ? '▲' : '▼'}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <ActionBtns row={item} tab="history" />
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className={s.detailRow}>
                      <td colSpan={14}>
                        <div className={s.expandDetail}>
                          <div className={s.detailGrid}>
                            <DetailCard title="ID записи" value={`#${item.id}`} />
                            <DetailCard title="Охранник въезда (А)" value={item.guard_name} sub={item.guard_phone} />
                            <DetailCard title="Охранник выезда (А)" value={item.exit_guard_name} sub={item.exit_guard_phone} />
                            <DetailCard title="Кто завершил (Б)" value={completedBy} sub={item.by_guard_phone} />
                            <DetailCard title="Время погрузки" value={item.duration_str_loading} />
                            <DetailCard title="Время доставки" value={item.duration_str} />
                            <DetailCard title="Итого" value={item.total_duration_str} />
                            <DetailCard title="Гео въезда (А)" value={<GeoBtn geo={item.geo_a} label="Открыть" />} />
                            <DetailCard title="Гео выезда (А)" value={<GeoBtn geo={item.geo_a_exit} label="Открыть" />} />
                            <DetailCard title="Гео прибытия (Б)" value={<GeoBtn geo={item.geo_b} label="Открыть" />} />
                            <DetailCard title="Фото въезда (А)" value={<PhotoBtn url={item.photo_a} label="Смотреть" />} />
                            <DetailCard title="Фото прибытия (Б)" value={<PhotoBtn url={item.photo_b} label="Смотреть" />} />
                            <DetailCard title="Статус водителя" value={item.driver_status} />
                            {notesClean && <DetailCard title="Примечания" value={notesClean} wide />}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
            {historyLoads.length === 0 && (
              <tr className={s.emptyRow}>
                <td colSpan={14}>История пуста</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={historyLoadsPage} count={historyLoadsCount} onPage={(p) => loadHistoryLoads(p, historyLoadsSearch)} />
    </>
  )

  // ─── Timeline chart ──────────────────────────────────────────────────────────

  const renderTimeline = () => {
    const data = (timeline && timeline.timeline) ? timeline.timeline : []
    const maxVal = Math.max(...data.map(d => d.total), 1)

    const W = 1000
    const H = 200
    const padL = 44, padR = 16, padT = 20, padB = 40
    const chartW = W - padL - padR
    const chartH = H - padT - padB
    const n = data.length
    const slotW = n > 0 ? chartW / n : chartW
    const barW = Math.max(4, slotW - 4)

    return (
      <div className={s.timelineSection}>
        {/* Stats + date picker row */}
        <div className={s.timelineTop}>
          <div className={s.timelineStats}>
            <div className={s.timelineStat}>
              <span className={s.timelineStatLabel}>С первого сканирования</span>
              <span className={s.timelineStatDate}>{timeline && timeline.first_scan_date ? moment(timeline.first_scan_date).format('DD.MM.YYYY') : '—'}</span>
            </div>
            <div className={s.timelineStatDivider} />
            <div className={s.timelineStat}>
              <span className={s.timelineStatLabel}>Всего создано записей</span>
              <span className={s.timelineStatBig}>{timeline ? timeline.total_scanned.toLocaleString('ru-RU') : '—'}</span>
            </div>
          </div>
          <div className={s.tlControls}>
            <div className={s.tlPresets}>
              {TL_PRESETS.map(p => (
                <button
                  key={p.key}
                  className={`${s.tlPresetBtn}${tlPreset === p.key ? ` ${s.tlPresetBtnActive}` : ''}`}
                  onClick={() => applyPreset(p)}
                  disabled={tlLoading}
                >{p.label}</button>
              ))}
            </div>
            <div className={s.timelineDateRange}>
              <label className={s.tlLabel}>с</label>
              <input
                type="date"
                value={tlDateFrom}
                max={tlDateTo}
                onChange={e => { setTlDateFrom(e.target.value); setTlPreset(null) }}
                className={s.tlDateInput}
              />
              <label className={s.tlLabel}>по</label>
              <input
                type="date"
                value={tlDateTo}
                min={tlDateFrom}
                max={todayStr}
                onChange={e => { setTlDateTo(e.target.value); setTlPreset(null) }}
                className={s.tlDateInput}
              />
              <button
                className={s.tlRefreshBtn}
                onClick={() => loadTimeline(tlDateFrom, tlDateTo)}
                disabled={tlLoading}
              >
                {tlLoading ? '…' : '↻'}
              </button>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className={s.timelineChartWrap}>
          {data.length === 0 && !tlLoading && (
            <div className={s.tlEmpty}>Нет данных за выбранный период</div>
          )}
          {tlLoading && <div className={s.tlLoadingOverlay}>Загрузка...</div>}
          {data.length > 0 && (
            <svg
              viewBox={`0 0 ${W} ${H}`}
              className={s.tlSvg}
              preserveAspectRatio="none"
            >
              {/* Grid lines */}
              {[0, 0.25, 0.5, 0.75, 1].map(ratio => {
                const y = padT + chartH * (1 - ratio)
                const val = Math.round(maxVal * ratio)
                return (
                  <g key={ratio}>
                    <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#eaecf2" strokeWidth="1" />
                    <text x={padL - 6} y={y + 4} textAnchor="end" fontSize="11" fill="#939db5" fontFamily="inherit">{val}</text>
                  </g>
                )
              })}

              {/* Bars */}
              {data.map((d, i) => {
                const x = padL + i * slotW + (slotW - barW) / 2
                const barH = d.total > 0 ? Math.max(2, (d.total / maxVal) * chartH) : 2
                const y = padT + chartH - barH
                const label = moment(d.date).format('DD.MM')
                const isWeekend = [0, 6].includes(new Date(d.date + 'T12:00:00').getDay())
                const showLabel = n <= 31 || i % Math.ceil(n / 25) === 0
                const barFill = d.total === 0 ? '#dde2ee' : isWeekend ? '#7c8db0' : '#0b1728'

                return (
                  <g key={d.date}>
                    <rect x={x} y={y} width={barW} height={barH} rx="2" fill={barFill}>
                      <title>{`${moment(d.date).format('DD.MM.YYYY')}: ${d.total} погр.`}</title>
                    </rect>
                    {d.total > 0 && barH > 18 && (
                      <text x={x + barW / 2} y={y - 3} textAnchor="middle" fontSize="9" fill="#555" fontFamily="inherit">
                        {d.total}
                      </text>
                    )}
                    {showLabel && (
                      <text
                        x={x + barW / 2}
                        y={H - padB + 14}
                        textAnchor="end"
                        fontSize="10"
                        fill={isWeekend ? '#7c8db0' : '#939db5'}
                        fontFamily="inherit"
                        transform={`rotate(-40, ${x + barW / 2}, ${H - padB + 14})`}
                      >
                        {label}
                      </text>
                    )}
                  </g>
                )
              })}

              {/* X axis line */}
              <line x1={padL} y1={padT + chartH} x2={W - padR} y2={padT + chartH} stroke="#dde2ee" strokeWidth="1" />
            </svg>
          )}
        </div>
      </div>
    )
  }

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className={s.loadsPanel}>
      {/* Header */}
      <div className={s.loadsPanelHeader}>
        <div className={s.loadsPanelTitle}>Погрузки</div>
      </div>

      {/* Timeline + stats */}
      {renderTimeline()}

      {/* Tab bar */}
      <div className={s.tabBar}>
        <button
          className={`${s.tabBtn}${subTab === 'in_loading' ? ` ${s.tabBtnActive}` : ''}`}
          onClick={() => setSubTab('in_loading')}
        >
          🔵 В погрузке
        </button>
        <button
          className={`${s.tabBtn}${subTab === 'in_route' ? ` ${s.tabBtnActive}` : ''}`}
          onClick={() => setSubTab('in_route')}
        >
          🟡 В пути
        </button>
        <button
          className={`${s.tabBtn}${subTab === 'history' ? ` ${s.tabBtnActive}` : ''}`}
          onClick={() => setSubTab('history')}
        >
          ✅ История
        </button>
      </div>

      {/* Body */}
      <div className={s.loadsPanelBody}>
        {subTab === 'in_loading' && renderLoadingLoads()}
        {subTab === 'in_route' && renderActiveLoads()}
        {subTab === 'history' && renderHistoryLoads()}
      </div>

      {/* Modal: Примечания */}
      {notesModal.isOpen && (
        <div className={s.modalOverlay}>
          <div className={s.modal}>
            <div className={s.modalTitle}>Примечания</div>
            <textarea
              className={s.modalTextarea}
              value={notesInput}
              onChange={(e) => setNotesInput(e.target.value)}
              placeholder="Введите примечания..."
            />
            <div className={s.modalActions}>
              <button className={s.modalBtnCancel} onClick={() => setNotesModal({ isOpen: false, loadId: null })}>
                Отмена
              </button>
              <button className={s.modalBtnPrimary} onClick={saveNotes} disabled={notesSaving}>
                {notesSaving ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Завершение поездки */}
      {completeTripModal.isOpen && (
        <div className={s.modalOverlay}>
          <div className={`${s.modal} ${s.dangerModal}`}>
            <div className={s.modalTitleDanger}>⚠ Завершение поездки</div>
            <div className={s.modalBody}>
              Текущий администратор будет записан как завершивший данную поездку.
            </div>
            <textarea
              className={s.modalTextarea}
              value={completeTripNotes}
              onChange={(e) => setCompleteTripNotes(e.target.value)}
              placeholder="Введите примечание (обязательно)..."
            />
            {userGeoLocation && (
              <div className={s.modalGeo}>
                📍 {userGeoLocation.lat.toFixed(6)}, {userGeoLocation.lng.toFixed(6)}
              </div>
            )}
            <div className={s.modalActions}>
              <button className={s.modalBtnCancel} onClick={() => setCompleteTripModal({ isOpen: false, loadId: null })}>
                Отмена
              </button>
              <button
                className={s.modalBtnDanger}
                onClick={handleCompleteTrip}
                disabled={completeTripSaving || !completeTripNotes.trim()}
              >
                {completeTripSaving ? 'Завершение...' : 'Завершить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Отметка выезда */}
      {markExitModal.isOpen && (
        <div className={s.modalOverlay}>
          <div className={`${s.modal} ${s.successModal}`}>
            <div className={s.modalTitleSuccess}>Подтверждение выезда</div>
            <div className={s.modalBody}>
              Вы действительно хотите отметить выезд для данной погрузки?
            </div>
            {userGeoLocation && (
              <div className={s.modalGeo}>
                📍 {userGeoLocation.lat.toFixed(6)}, {userGeoLocation.lng.toFixed(6)}
              </div>
            )}
            <div className={s.modalActions}>
              <button className={s.modalBtnCancel} onClick={() => setMarkExitModal({ isOpen: false, loadId: null })}>
                Отмена
              </button>
              <button className={s.modalBtnSuccess} onClick={handleMarkExit} disabled={markExitSaving}>
                {markExitSaving ? 'Обработка...' : 'Подтвердить выезд'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default LoadsPanel
